import { z } from 'zod';
import { usePatientConfiguration } from '@/features/patient-configuration/api';
import { toast } from 'sonner';
import { useForm } from 'react-hook-form';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Save, Send, X } from 'lucide-react';

import { Form } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { useCreatePatient } from '../api/patients-api';
import { P, PermissionGuard } from '@/features/permissions';
import { PatientFormFields } from '../components/PatientFormFields';
import { usePageHeaderSetter } from '@/components/layout/PageHeaderContext';
import {
  EMPTY_PATIENT_FORM,
  formValuesToMetadata,
  patientFormSchema,
  type PatientFormValues,
} from '../schemas/patientForm';

// ─── Component ───────────────────────────────────────────────────

export function NewPatientPage() {
  return (
    <PermissionGuard permission={P.CREATE_PATIENT}>
      <NewPatientPageContent />
    </PermissionGuard>
  );
}

function NewPatientPageContent() {
  const navigate = useNavigate();
  const setPageHeader = usePageHeaderSetter();
  const formRef = useRef<HTMLFormElement>(null);
  const submitTypeRef = useRef<'draft' | 'submit'>('draft');
  const [isLoading, setIsLoading] = useState(false);
  const createPatient = useCreatePatient();
  const configuration = usePatientConfiguration();
  const automaticMrn = configuration.data && configuration.data.mrn.mode !== 'manual';

  const form = useForm<PatientFormValues>({
    defaultValues: EMPTY_PATIENT_FORM,
    resolver: zodResolver(
      automaticMrn ? patientFormSchema.extend({ medical_record_number: z.string() }) : patientFormSchema,
    ),
  });

  // ─── Header actions ──────────────────────────────────────────

  const handleCancel = () => {
    navigate({ to: '/patients' });
  };

  const handleSaveDraft = () => {
    submitTypeRef.current = 'draft';
    formRef.current?.requestSubmit();
  };

  const handleSubmit = () => {
    submitTypeRef.current = 'submit';
    formRef.current?.requestSubmit();
  };

  useEffect(() => {
    setPageHeader({
      title: 'Add New Patient',
      actions: (
        <div className="flex gap-2">
          <Button
            variant="ghost"
            aria-label="Cancel"
            disabled={isLoading}
            className="hidden md:flex"
            onClick={handleCancel}>
            <X className="h-4 w-4" />
            Cancel
          </Button>
          <Button variant="outline" disabled={isLoading} aria-label="Save as Draft" onClick={handleSaveDraft}>
            {isLoading && submitTypeRef.current === 'draft' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span className="hidden md:inline">
              {isLoading && submitTypeRef.current === 'draft' ? 'Saving...' : 'Save Draft'}
            </span>
          </Button>
          <Button disabled={isLoading} aria-label="Submit Patient" onClick={handleSubmit}>
            {isLoading && submitTypeRef.current === 'submit' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            <span className="hidden md:inline">
              {isLoading && submitTypeRef.current === 'submit' ? 'Submitting...' : 'Submit'}
            </span>
          </Button>
        </div>
      ),
    });

    return () => setPageHeader(null);
  }, [navigate, setPageHeader, isLoading]);

  // ─── Form submission ─────────────────────────────────────────

  const onSubmit = async (values: PatientFormValues) => {
    setIsLoading(true);
    try {
      await createPatient.mutateAsync({
        metadata_: formValuesToMetadata(values),
        medical_record_number: values.medical_record_number || undefined,
        patient_source: submitTypeRef.current === 'draft' ? 'draft' : 'manual',
      });
      toast.success(submitTypeRef.current === 'draft' ? 'Patient saved as draft' : 'Patient created successfully');
      navigate({ to: '/patients' });
    } catch {
      toast.error('Failed to create patient');
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────

  return (
    <div className="relative mt-4 px-4">
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-lg font-medium">
              {submitTypeRef.current === 'draft' ? 'Saving patient...' : 'Creating patient...'}
            </p>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-4xl">
        <Form {...form}>
          <form ref={formRef} className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
            <p className="text-sm text-muted-foreground">
              {automaticMrn
                ? 'Leave MRN blank to assign it using your practice preferences.'
                : 'Enter a medical record number.'}
            </p>
            <PatientFormFields form={form} mrnOptional={!!automaticMrn} />
          </form>
        </Form>
      </div>
    </div>
  );
}
